import DOMPurify from 'dompurify';

/**
 * Generates a URL-safe slug from a title
 */
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-+|-+$/g, ''); // Trim hyphens from start/end
};

/**
 * Calculates reading time in minutes based on word count
 * Average reading speed: 200 words per minute
 */
export const calculateReadingTime = (content: string): number => {
  const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return Math.max(1, minutes); // Minimum 1 minute
};

/**
 * Extracts a plain text excerpt from HTML content
 */
export const extractExcerpt = (content: string, maxLength: number = 160): string => {
  const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
  const cleaned = text.replace(/\s+/g, ' ').trim(); // Normalize whitespace
  
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  
  // Truncate at word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return truncated.substring(0, lastSpace) + '...';
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'strong', 'em', 'u', 'strike',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class']
  });
};

/**
 * Formats a date for display
 */
export const formatPublishedDate = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Parses blog content and generates TOC in a single pass (performance optimized)
 */
export const parseContentWithTOC = (content: string): { html: string; tocItems: Array<{ id: string; text: string; level: number }> } => {
  const sanitized = sanitizeHtml(content);
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');
  const headings = doc.querySelectorAll('h2, h3');
  
  const tocItems: Array<{ id: string; text: string; level: number }> = [];
  
  headings.forEach((heading) => {
    const text = heading.textContent || '';
    const id = generateSlug(text);
    heading.id = id;
    
    tocItems.push({
      id,
      text,
      level: parseInt(heading.tagName.substring(1))
    });
  });
  
  return {
    html: doc.body.innerHTML,
    tocItems
  };
};

/**
 * Generates table of contents from HTML headings
 */
export const generateTableOfContents = (html: string): Array<{ id: string; text: string; level: number }> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const headings = doc.querySelectorAll('h2, h3');
  
  return Array.from(headings).map((heading, index) => {
    const id = `heading-${index}`;
    heading.id = id;
    
    return {
      id,
      text: heading.textContent || '',
      level: parseInt(heading.tagName.substring(1))
    };
  });
};

/**
 * Formats view count for display
 */
export const formatViewCount = (count: number): string => {
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
};

/**
 * Generates social share URLs
 */
export const getSocialShareUrl = (platform: string, url: string, title: string): string => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  
  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    case 'email':
      return `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
    default:
      return url;
  }
};
